import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Download, Upload, RotateCcw, Trash2, User, Sparkles, KeyRound } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useWedding } from "@/context/WeddingContext";
import { PageHeader } from "@/components/shared";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { toast } from "sonner";

export default function Settings() {
  const { user, setUser, logout, hasAccess, setHasAccess } = useAuth();
  const { wedding, load } = useWedding();
  const navigate = useNavigate();
  const [name, setName] = useState(user?.name || "");
  const [code, setCode] = useState("");
  const [redeeming, setRedeeming] = useState(false);

  useEffect(() => { setName(user?.name || ""); }, [user]);

  const saveName = async () => {
    try { const { data } = await api.put("/auth/profile", { name }); setUser(data.user); toast.success("Profile updated"); }
    catch { toast.error("Couldn't update profile."); }
  };

  const exportBackup = async () => {
    try {
      const { data } = await api.get("/export");
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = `planmywedding-backup-${new Date().toISOString().slice(0, 10)}.json`; a.click();
      URL.revokeObjectURL(url);
      toast.success("Backup exported");
    } catch { toast.error("Export failed."); }
  };

  const importBackup = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = async () => {
      try {
        const payload = JSON.parse(reader.result);
        await api.post("/import", payload);
        await load();
        toast.success("Backup imported successfully.");
        setTimeout(() => window.location.reload(), 800);
      } catch { toast.error("That backup file couldn't be imported."); }
    };
    reader.readAsText(file);
    e.target.value = "";
  };

  const resetPlanner = async () => {
    try { await api.post("/reset"); toast.success("Planner reset."); setTimeout(() => window.location.reload(), 600); }
    catch { toast.error("Reset failed."); }
  };

  const loadDemo = async () => {
    try { await api.post("/demo"); toast.success("Demo planner loaded."); setTimeout(() => window.location.reload(), 600); }
    catch { toast.error("Couldn't load demo."); }
  };

  const deleteAccount = async () => {
    try { await api.delete("/account"); toast.success("Account deleted."); await logout(); navigate("/"); }
    catch { toast.error("Deletion failed."); }
  };

  const redeem = async () => {
    if (!code.trim()) { toast.error("Please enter your access code."); return; }
    setRedeeming(true);
    try {
      const { data } = await api.post("/redeem", { code });
      setUser(data.user); setHasAccess(true); setCode("");
      toast.success("Access unlocked — you now have lifetime access!");
    } catch (e) { toast.error(e.response?.data?.detail || "That code isn't valid."); }
    finally { setRedeeming(false); }
  };

  return (
    <div className="max-w-2xl space-y-6">
      <PageHeader title="Settings" subtitle="Manage your account, planner data and preferences." testId="settings-header" />

      <div className="rounded-2xl bg-card border border-border p-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2"><KeyRound className="h-5 w-5 text-accent" /><h3 className="text-xl tracking-tight text-primary">Access</h3></div>
          <span className={`text-xs px-3 py-1 rounded-full ${hasAccess ? "bg-accent/20 text-accent-foreground" : "bg-secondary text-muted-foreground"}`} data-testid="access-badge">
            {hasAccess ? `Lifetime access · ${user?.entitlement?.plan || "lifetime"}` : "Demo / not unlocked"}
          </span>
        </div>
        {hasAccess ? (
          <p className="text-sm text-muted-foreground mt-3">You have full lifetime access to every planning tool. Thank you for your purchase.</p>
        ) : (
          <>
            <p className="text-sm text-muted-foreground mt-3 mb-4">Enter the access code from your Etsy order to unlock your own planner forever.</p>
            <div className="flex flex-wrap items-end gap-3">
              <div className="flex-1 min-w-[200px]">
                <Label className="text-xs text-muted-foreground">Access code</Label>
                <Input value={code} onChange={(e) => setCode(e.target.value.toUpperCase())} placeholder="PMW-XXXX-XXXX" className="rounded-xl mt-1 tracking-widest font-medium" data-testid="settings-redeem-input" />
              </div>
              <Button onClick={redeem} disabled={redeeming} className="rounded-full" data-testid="settings-redeem-btn">{redeeming ? "Unlocking…" : "Unlock access"}</Button>
            </div>
          </>
        )}
      </div>

      <div className="rounded-2xl bg-card border border-border p-6">
        <div className="flex items-center gap-2 mb-4"><User className="h-5 w-5 text-accent" /><h3 className="text-xl tracking-tight text-primary">Account</h3></div>
        <div className="grid sm:grid-cols-2 gap-4">
          <div><Label className="text-xs text-muted-foreground">Name</Label><Input value={name} onChange={(e) => setName(e.target.value)} className="rounded-xl mt-1" data-testid="settings-name" /></div>
          <div><Label className="text-xs text-muted-foreground">Email</Label><Input value={user?.email || ""} disabled className="rounded-xl mt-1 opacity-70" /></div>
        </div>
        <Button onClick={saveName} className="rounded-full mt-4" data-testid="save-profile">Save profile</Button>
      </div>

      <div className="rounded-2xl bg-card border border-border p-6">
        <div className="flex items-center gap-2 mb-1"><Download className="h-5 w-5 text-accent" /><h3 className="text-xl tracking-tight text-primary">Data</h3></div>
        <p className="text-sm text-muted-foreground mb-4">Export a full backup of your planner, restore from a file, or start fresh.</p>
        <div className="flex flex-wrap gap-3">
          <Button variant="outline" onClick={exportBackup} className="rounded-full" data-testid="export-btn"><Download className="h-4 w-4 mr-1.5" />Export Backup</Button>
          <label>
            <input type="file" accept="application/json" onChange={importBackup} className="hidden" data-testid="import-input" />
            <Button variant="outline" className="rounded-full" asChild><span><Upload className="h-4 w-4 mr-1.5" />Import Backup</span></Button>
          </label>
          {!wedding?.is_demo && <Button variant="outline" onClick={loadDemo} className="rounded-full" data-testid="load-demo"><Sparkles className="h-4 w-4 mr-1.5" />Load Demo Data</Button>}

          <AlertDialog>
            <AlertDialogTrigger asChild><Button variant="outline" className="rounded-full text-destructive border-destructive/30" disabled={!hasAccess} data-testid="reset-btn"><RotateCcw className="h-4 w-4 mr-1.5" />Reset Planner</Button></AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader><AlertDialogTitle>Reset your planner?</AlertDialogTitle><AlertDialogDescription>This will permanently remove the wedding planning data in this planner. This cannot be undone.</AlertDialogDescription></AlertDialogHeader>
              <AlertDialogFooter><AlertDialogCancel>Cancel</AlertDialogCancel><AlertDialogAction onClick={resetPlanner} data-testid="confirm-reset">Reset planner</AlertDialogAction></AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      </div>

      <div className="rounded-2xl bg-card border border-destructive/20 p-6">
        <div className="flex items-center gap-2 mb-1"><Trash2 className="h-5 w-5 text-destructive" /><h3 className="text-xl tracking-tight text-destructive">Danger zone</h3></div>
        <p className="text-sm text-muted-foreground mb-4">Deleting your account permanently removes your planner and all associated data.</p>
        <AlertDialog>
          <AlertDialogTrigger asChild><Button variant="destructive" className="rounded-full" data-testid="delete-account-btn">Delete Account</Button></AlertDialogTrigger>
          <AlertDialogContent>
            <AlertDialogHeader><AlertDialogTitle>Delete your account?</AlertDialogTitle><AlertDialogDescription>This permanently deletes your account and all planner data. This cannot be undone.</AlertDialogDescription></AlertDialogHeader>
            <AlertDialogFooter><AlertDialogCancel>Cancel</AlertDialogCancel><AlertDialogAction onClick={deleteAccount} data-testid="confirm-delete-account">Delete permanently</AlertDialogAction></AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </div>
  );
}
